/**
 * Glossary-view (layout_mode = "glossary") — Sharp Attribuuttypes.
 *
 * Eigen render-pad (geen React Flow): een alfabetisch gesorteerde tabel met
 * alle zichtbare `sharp.AttributeType`-concepten (gedeelde glossary-definities:
 * meertalige naam + meertalige definitie). Toevoegen via popup, Verwijderen met
 * bevestiging. Fase 2 voegt de referentie vanuit Attribute/Identifier toe +
 * de "niet verwijderbaar zolang in gebruik"-check.
 */

import type { Concept } from "@ae/domain";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import { resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";
import { BilingualField } from "../attribute-panel/BilingualField";
import { confirmDialog } from "../confirmDialog";
import { ZoomPan } from "./ZoomPan";

const ATTRIBUTE_TYPE = "sharp.AttributeType";

// Render-pad voor zowel de virtuele view (Editor, glossaryFocused) als een
// eventueel los glossary-diagram (Canvas-dispatch). Geen props nodig — de
// inhoud komt volledig uit de store.
export function GlossaryCanvas() {
  const { t, i18n } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: de lijst is te lezen; toevoegen, bewerken en wissen niet.
  const readOnly = useSession((s) => s.readOnly);
  const revision = useWorkspaceData((s) => s.revision);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Bewerk-id: null = nieuw toevoegen, anders bewerken we dat attribuuttype.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Formuliervelden. De NAAM is single-language (technische vaste waarde — geen
  // meertalige variant, geen auto-translate). De DEFINITIE blijft meertalig.
  const [name, setName] = useState("");
  const [defNl, setDefNl] = useState("");
  const [defEn, setDefEn] = useState("");

  // Alle zichtbare attribuuttypes uit de store, alfabetisch op de actieve taal.
  const rows = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    return store
      .listConcepts()
      .filter((c) => c.typeId === ATTRIBUTE_TYPE && !c.tombstoneInBranch)
      .sort((a, b) => resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)));
  }, [revision, lang]);

  const resetForm = () => {
    setName("");
    setDefNl("");
    setDefEn("");
    setAdding(false);
    setEditingId(null);
  };

  // Read-only check: een attribuuttype in een gedeelde folder waar de
  // huidige (non-Base) WP geen write-rechten op heeft, mag niet bewerkt.
  const isReadOnly = (c: Concept | null | undefined): boolean => c?.editable === false;

  const startEdit = (c: Concept) => {
    if (isReadOnly(c)) return; // alleen-lezen folder-concept — geen popup
    if (useSession.getState().readOnly) return;
    setName(c.name.nl ?? c.name.en ?? "");
    const d = (c.attributes as Record<string, unknown> | undefined)?.definition as
      | { nl?: string; en?: string }
      | undefined;
    setDefNl(d?.nl ?? "");
    setDefEn(d?.en ?? "");
    setEditingId(c.id);
    setAdding(true);
  };

  const saveType = async () => {
    if (!workspaceId || !branchId || busy) return;
    if (useSession.getState().readOnly) return;
    const nm = name.trim();
    if (!nm) return;
    setBusy(true);
    try {
      // Single-language naam → nl=en zodat 't taal-onafhankelijk toont.
      const body = {
        name: { nl: nm, en: nm },
        attributes: { definition: { nl: defNl.trim(), en: defEn.trim() } },
      };
      if (editingId) {
        await api.patchConcept(workspaceId, branchId, editingId, body);
      } else {
        await api.createConcept(workspaceId, branchId, { type_id: ATTRIBUTE_TYPE, ...body });
      }
      resetForm();
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    if (!workspaceId || !branchId || busy || !selectedId) return;
    if (useSession.getState().readOnly) return;
    const c = useWorkspaceData.getState().store.getConcept(selectedId);
    const label = c ? resolveBilingual(c.name, lang) : "";
    // Fase 2: hier komt de "in gebruik als referentie?"-blokkade. Nu nog kale
    // bevestiging.
    if (
      !(await confirmDialog({
        message: t("glossary.confirmDelete", { name: label }),
        danger: true,
      }))
    )
      return;
    setBusy(true);
    try {
      await useWorkspaceData.getState().removeConcept(workspaceId, branchId, selectedId);
      setSelectedId(null);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const definitionOf = (c: Concept): string => {
    const d = (c.attributes as Record<string, unknown> | undefined)?.definition;
    if (d && typeof d === "object") {
      return resolveBilingual(d as { nl?: string; en?: string }, lang);
    }
    return "";
  };

  const selectedConcept = selectedId ? rows.find((r) => r.id === selectedId) : null;
  const selectedReadOnly = isReadOnly(selectedConcept);

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="flex items-center gap-2 border-slate-200 border-b px-4 py-2">
        <span className="font-medium text-slate-700 text-sm">{t("glossary.title")}</span>
        <span className="text-slate-400 text-xs">
          {t("glossary.count", { count: rows.length })}
        </span>
        <div className="ml-auto flex items-center gap-2" hidden={readOnly}>
          <button
            type="button"
            disabled={busy}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={() => {
              resetForm();
              setAdding(true);
            }}
          >
            {t("glossary.add")}
          </button>
          <button
            type="button"
            disabled={busy || !selectedId || selectedReadOnly}
            title={selectedReadOnly ? t("glossary.readOnly") : undefined}
            className="rounded border border-slate-300 px-3 py-1 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40"
            onClick={() => {
              const c = selectedId
                ? useWorkspaceData.getState().store.getConcept(selectedId)
                : null;
              if (c) startEdit(c);
            }}
          >
            {t("glossary.edit")}
          </button>
          <button
            type="button"
            disabled={busy || !selectedId || selectedReadOnly}
            title={selectedReadOnly ? t("glossary.readOnly") : undefined}
            className="rounded border border-slate-300 px-3 py-1 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40"
            onClick={() => void deleteSelected()}
          >
            {t("glossary.delete")}
          </button>
        </div>
      </div>

      <ZoomPan className="min-h-0 flex-1" contentWidth="full">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-slate-100">
            <tr className="text-left text-slate-600">
              <th className="border-slate-200 border-b px-4 py-2 font-medium">
                {t("glossary.colName")}
              </th>
              <th className="border-slate-200 border-b px-4 py-2 font-medium">
                {t("glossary.colDefinition")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-4 py-6 text-center text-slate-400">
                  {t("glossary.empty")}
                </td>
              </tr>
            ) : (
              rows.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  onDoubleClick={() => startEdit(c)}
                  className={`cursor-pointer ${
                    selectedId === c.id ? "bg-blue-50" : "hover:bg-slate-50"
                  }`}
                >
                  <td className="border-slate-100 border-b px-4 py-1.5 font-medium text-slate-800">
                    {resolveBilingual(c.name, lang) || t("glossary.unnamed")}
                    {isReadOnly(c) ? (
                      <span className="ml-1.5 text-slate-400" title={t("glossary.readOnly")}>
                        🔒
                      </span>
                    ) : null}
                  </td>
                  <td className="border-slate-100 border-b px-4 py-1.5 text-slate-600">
                    {definitionOf(c)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ZoomPan>

      {adding && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30">
          <div className="w-[420px] rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
            <div className="mb-3 font-medium text-slate-700 text-sm">
              {editingId ? t("glossary.editTitle") : t("glossary.addTitle")}
            </div>
            <div className="flex flex-col gap-3">
              {/* Naam = single-language (vaste technische waarde, geen vertaling). */}
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-slate-500">{t("glossary.name")}</span>
                <input
                  className="rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              {/* Definitie blijft meertalig via BilingualField (auto-translate). */}
              <BilingualField
                kind="description"
                multiline
                labelNl={t("glossary.defNl")}
                labelEn={t("glossary.defEn")}
                valueNl={defNl}
                valueEn={defEn}
                onChange={(lng, v) => (lng === "nl" ? setDefNl(v) : setDefEn(v))}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-slate-600 text-sm hover:bg-slate-50"
                onClick={resetForm}
              >
                {t("glossary.cancel")}
              </button>
              <button
                type="button"
                disabled={busy || !name.trim()}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => void saveType()}
              >
                {t("glossary.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
